import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/apiClient';
import './ScreeningAutomationPanel.css';

const DEFAULTS = {
    screening_auto_reject_enabled: true,
    screening_reject_below_score: 35,
    screening_auto_reject_email_enabled: true,
};

const ScreeningAutomationPanel = ({ jobId, className = '' }) => {
    const [settings, setSettings] = useState(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');
    const [applySummary, setApplySummary] = useState('');

    useEffect(() => {
        fetchSettings();
    }, [jobId]);

    const fetchSettings = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/screening-settings`);
            if (response.ok) {
                const data = await response.json();
                setSettings({ ...DEFAULTS, ...data });
            } else {
                setError('Could not load screening settings.');
            }
        } catch (err) {
            console.error('Error fetching screening settings:', err);
            setError('Could not load screening settings.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setSettings((prev) => ({ ...prev, [field]: value }));
        setSaved(false);
        setApplySummary('');
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setApplySummary('');
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/screening-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    screening_auto_reject_enabled: Boolean(settings.screening_auto_reject_enabled),
                    screening_reject_below_score: Number(settings.screening_reject_below_score),
                    screening_auto_reject_email_enabled: Boolean(
                        settings.screening_auto_reject_email_enabled
                    ),
                }),
            });
            if (response.ok) {
                const data = await response.json();
                setSettings({
                    ...DEFAULTS,
                    screening_auto_reject_enabled: data.screening_auto_reject_enabled,
                    screening_reject_below_score: data.screening_reject_below_score,
                    screening_auto_reject_email_enabled: data.screening_auto_reject_email_enabled,
                });
                setSaved(true);
                const applied = data.applied || {};
                if (applied.emailed) {
                    setApplySummary(
                        `${applied.emailed} rejection email${applied.emailed === 1 ? '' : 's'} sent`
                    );
                } else if (applied.email_failed) {
                    setApplySummary(
                        `${applied.email_failed} email send failed`
                    );
                } else if (applied.skipped_no_email) {
                    setApplySummary(
                        `${applied.skipped_no_email} skipped (no email)`
                    );
                } else if (applied.rejected) {
                    setApplySummary('Saved. Candidates updated.');
                } else {
                    setApplySummary('Saved.');
                }
                setTimeout(() => setSaved(false), 2500);
            } else {
                const body = await response.json().catch(() => ({}));
                setError(body.detail || body.error || 'Failed to save screening settings');
            }
        } catch (err) {
            console.error('Error saving screening settings:', err);
            setError('Failed to save screening settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div
                id="job-screening-settings"
                className={`screening-automation-panel ${className}`.trim()}
            >
                Loading screening settings…
            </div>
        );
    }

    const autoRejectOn = Boolean(settings.screening_auto_reject_enabled);

    return (
        <div
            id="job-screening-settings"
            className={`screening-automation-panel ${className}`.trim()}
        >
            <h3>AI Screening Automation</h3>
            <p className="help-text">
                Automatically mark applicants as screening-rejected when their AI fit score is below
                your threshold. Saving also applies to candidates already analyzed for this job.
                When email is on, newly rejected candidates with a valid email address receive a
                rejection email.
            </p>

            <div className="toggle-row">
                <span className="toggle-label">Auto-reject candidates below score threshold</span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={autoRejectOn}
                    className={`tv-toggle${autoRejectOn ? ' is-on' : ''}`}
                    onClick={() =>
                        handleChange('screening_auto_reject_enabled', !autoRejectOn)
                    }
                >
                    <span className="tv-toggle-knob" />
                </button>
            </div>

            <div className={`threshold-row ${autoRejectOn ? '' : 'disabled'}`}>
                <label htmlFor={`screening-reject-below-${jobId}`}>
                    Reject when fit score is below
                </label>
                <div className="threshold-input-group">
                    <input
                        id={`screening-reject-below-${jobId}`}
                        type="number"
                        min={0}
                        max={100}
                        disabled={!autoRejectOn}
                        value={settings.screening_reject_below_score}
                        onChange={(e) =>
                            handleChange(
                                'screening_reject_below_score',
                                Math.max(0, Math.min(100, Number(e.target.value) || 0))
                            )
                        }
                    />
                    <span className="threshold-suffix">/ 100</span>
                </div>
                <small>
                    Candidates with score &lt; {settings.screening_reject_below_score} are marked
                    screening-rejected. Ineligible candidates are still rejected even if auto-reject
                    is off.
                </small>
            </div>

            <div className={`toggle-row ${autoRejectOn ? '' : 'disabled'}`}>
                <span className="toggle-label">Send automatic rejection email</span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={Boolean(settings.screening_auto_reject_email_enabled)}
                    disabled={!autoRejectOn}
                    className={`tv-toggle${
                        settings.screening_auto_reject_email_enabled ? ' is-on' : ''
                    }`}
                    onClick={() => {
                        if (!autoRejectOn) return;
                        handleChange(
                            'screening_auto_reject_email_enabled',
                            !settings.screening_auto_reject_email_enabled
                        );
                    }}
                >
                    <span className="tv-toggle-knob" />
                </button>
            </div>

            {error && <div className="screening-error">{error}</div>}
            {applySummary && <div className="screening-apply-summary">{applySummary}</div>}

            <div className="screening-actions">
                <button
                    type="button"
                    className="save-button"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving…' : 'Save screening settings'}
                </button>
                {saved && <span className="save-success">Saved</span>}
            </div>
        </div>
    );
};

export default ScreeningAutomationPanel;
